import { eFeePropertyNames } from 'types/order/fees/eFeePropertyNames';
import React, { useEffect } from 'react';
import { NumberField } from 'ui-toolkit/components/NumberField/NumberField';
import { Field, FieldProps, useField } from 'formik';
import { SelectField } from 'ui-toolkit/components/SelectField/SelectField';
import { lenderPaidOptions } from 'components/cdBuilder/utilities/lenderPaidOptions';
import { useHasFeatureFlag } from 'selectors/useHasFeatureFlag';

export interface ISellerFieldsProps {
  properties: { [key: string]: boolean };
  baseName: string;
  readOnly: boolean;
}

export const SellerFields: React.FC<ISellerFieldsProps> = ({ properties, baseName, readOnly }) => {
  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');
  const sendingToLabel = isZillowClosingEnabled ? 'Sending to Zillow Closing' : 'Sending to Spruce';

  const [{ value: sellerPaidAmount }, , { setValue }] = useField<number | undefined>(
    `${baseName}.numericFeePropertyValues.${eFeePropertyNames.SellerPaidAmount}`
  );
  const [{ value: othersPaidSeller }] = useField<number | undefined>(
    `${baseName}.numericFeePropertyValues.${eFeePropertyNames.OthersPaidSeller}`
  );
  const [{ value: sellerPaidAtClosing }] = useField<number | undefined>(
    `${baseName}.numericFeePropertyValues.${eFeePropertyNames.SellerPaidAtClosing}`
  );
  const hasSellerPaidAmount = properties[eFeePropertyNames.SellerPaidAmount];
  const hasSellerPaidAtClosing = properties[eFeePropertyNames.SellerPaidAtClosing];
  const hasOthersPaidSeller = properties[eFeePropertyNames.OthersPaidSeller];

  useEffect(() => {
    if (hasSellerPaidAmount && hasSellerPaidAtClosing && hasOthersPaidSeller) {
      const total = (othersPaidSeller || 0) + (sellerPaidAtClosing || 0);
      if (total !== sellerPaidAmount) {
        setValue(total);
      }
    }
  }, [
    othersPaidSeller,
    sellerPaidAmount,
    sellerPaidAtClosing,
    setValue,
    hasSellerPaidAmount,
    hasSellerPaidAtClosing,
    hasOthersPaidSeller,
  ]);

  return (
    <>
      {properties[eFeePropertyNames.SellerPaidAmount] && (
        <div className={'flex-shrink-0 flex-grow  basis-1/6 p-1'}>
          <NumberField
            name={`${baseName}.numericFeePropertyValues.${eFeePropertyNames.SellerPaidAmount}`}
            label={'Seller Paid'}
            hintText={properties[eFeePropertyNames.SellerPaidAtClosing] ? 'Total Amount' : 'At closing'}
            placeholder="$0.00"
            mode="currency"
            decimalScale={2}
            readOnly={readOnly || properties[eFeePropertyNames.SellerPaidAtClosing]}
          />
        </div>
      )}
      {properties[eFeePropertyNames.SellerPaidAtClosing] && (
        <div className={'flex-shrink-0 flex-grow basis-1/6  p-1'}>
          <NumberField
            name={`${baseName}.numericFeePropertyValues.${eFeePropertyNames.SellerPaidAtClosing}`}
            label={(!properties[eFeePropertyNames.SellerPaidAmount] && 'Seller Paid') || <div>&nbsp;</div>}
            hintText={'At closing'}
            placeholder="$0.00"
            mode="currency"
            decimalScale={2}
            readOnly={readOnly}
          />
        </div>
      )}
      {properties[eFeePropertyNames.SellerPaidBeforeClosing] && (
        <div className={'flex-shrink-0 flex-grow basis-1/6  p-1'}>
          <NumberField
            name={`${baseName}.numericFeePropertyValues.${eFeePropertyNames.SellerPaidBeforeClosing}`}
            label={(!properties[eFeePropertyNames.SellerPaidAtClosing] && 'Seller Paid') || <div>&nbsp;</div>}
            hintText={'Before closing'}
            placeholder="$0.00"
            mode="currency"
            decimalScale={2}
            readOnly={readOnly}
          />
        </div>
      )}
      {properties[eFeePropertyNames.OthersPaidSeller] && (
        <div className={'flex-shrink-0 flex-grow basis-1/6  p-1'}>
          <NumberField
            name={`${baseName}.numericFeePropertyValues.${eFeePropertyNames.OthersPaidSeller}`}
            label={'Others Paid'}
            hintText={'For Seller'}
            placeholder="$0.00"
            mode="currency"
            decimalScale={2}
            readOnly={readOnly}
          />
        </div>
      )}
      {properties[eFeePropertyNames.SendingToSpruceSeller] && (
        <div className={'flex-shrink-0 flex-grow basis-1/2  p-1'}>
          <Field name={`${baseName}.numericFeePropertyValues.${eFeePropertyNames.OthersPaidSeller}`}>
            {({ field }: FieldProps) => (
              <SelectField
                label={sendingToLabel}
                hintText={'For Seller'}
                options={lenderPaidOptions(isZillowClosingEnabled)}
                name={`${baseName}.stringFeePropertyValues.${eFeePropertyNames.SendingToSpruceSeller}`}
                isClearable={true}
                isDisabled={
                  readOnly ||
                  !properties[eFeePropertyNames.SendingToSpruceSeller] ||
                  typeof field.value === 'undefined' ||
                  field.value <= 0
                }
              />
            )}
          </Field>
        </div>
      )}
    </>
  );
};
